import React, { useState } from "react";
import { postApi } from "../../services/api/postApi";
import "./EditPostModal.css";

const EditPostModal = ({ post, isOpen, onClose, onSuccess }) => {
  const [content, setContent] = useState(post.content || "");
  const [tags, setTags] = useState(post.tags?.join(", ") || "");
  const [questionTitle, setQuestionTitle] = useState(
    post.question?.title || ""
  );
  const [questionTags, setQuestionTags] = useState(
    post.question?.tags?.join(", ") || ""
  );
  const [loading, setLoading] = useState(false);
  const isQuestion = post.postType === "question";

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate based on post type
    if (isQuestion) {
      if (!questionTitle.trim()) {
        alert("Question title cannot be empty");
        return;
      }
      if (questionTitle.trim().length > 300) {
        alert("Question title must be 300 characters or less");
        return;
      }
    } else {
      if (!content.trim()) {
        alert("Content cannot be empty");
        return;
      }
    }

    setLoading(true);

    try {
      const updateData = {};

      if (isQuestion) {
        // Update question
        updateData.content = content.trim();
        // 🔥 CRITICAL: Preserve ALL existing question data to prevent data loss
        updateData.question = {
          ...post.question, // Preserve upvotes, downvotes, answers, acceptedAnswer, bounty, views
          title: questionTitle.trim(),
          tags: questionTags
            .split(",")
            .map((t) => t.trim())
            .filter(Boolean)
            .slice(0, 5), // Max 5 tags per spec
        };
      } else {
        // Update regular post
        updateData.content = content.trim();
        updateData.tags = tags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean);
      }

      const response = await postApi.updatePost(post._id, updateData);

      onSuccess(response.data.post);
      onClose();
    } catch (error) {
      const errorMsg = error.response?.data?.message || "Failed to update post";
      alert(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="epm-overlay" onClick={onClose}>
      <div className="epm-content" onClick={(e) => e.stopPropagation()}>
        <div className="epm-header">
          <h3 className="epm-title">Edit Post</h3>
          <button
            className="epm-close-btn"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="epm-form">
          {isQuestion ? (
            <>
              {/* Question Title */}
              <div className="epm-form-group">
                <label htmlFor="question-title">Question Title *</label>
                <input
                  type="text"
                  id="question-title"
                  value={questionTitle}
                  onChange={(e) => setQuestionTitle(e.target.value)}
                  maxLength={300}
                  placeholder="What is your question?"
                  required
                />
                <div className="epm-char-count">{questionTitle.length}/300</div>
              </div>

              {/* Question Details */}
              <div className="epm-form-group">
                <label htmlFor="content">Details (optional)</label>
                <textarea
                  id="content"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  rows={6}
                  maxLength={10000}
                  placeholder="Provide additional context..."
                />
                <div className="epm-char-count">{content.length}/10000</div>
              </div>

              {/* Question Tags */}
              <div className="epm-form-group">
                <label htmlFor="question-tags">Tags (optional)</label>
                <input
                  type="text"
                  id="question-tags"
                  value={questionTags}
                  onChange={(e) => setQuestionTags(e.target.value)}
                  placeholder="javascript, react, api (comma-separated)"
                />
                <small className="epm-hint">Add up to 5 tags</small>
              </div>
            </>
          ) : (
            <>
              {/* Regular Post Content */}
              <div className="epm-form-group">
                <label htmlFor="content">Content</label>
                <textarea
                  id="content"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  rows={8}
                  maxLength={5000}
                  placeholder="What's on your mind?"
                  required
                />
                <div className="epm-char-count">{content.length}/5000</div>
              </div>

              {/* Regular Post Tags */}
              <div className="epm-form-group">
                <label htmlFor="tags">Tags (optional)</label>
                <input
                  type="text"
                  id="tags"
                  value={tags}
                  onChange={(e) => setTags(e.target.value)}
                  placeholder="AI, research, machine-learning (comma-separated)"
                />
                <small className="epm-hint">
                  Add tags to help others discover your post
                </small>
              </div>
            </>
          )}

          <div className="epm-actions">
            <button
              type="button"
              onClick={onClose}
              className="epm-cancel-btn"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="epm-submit-btn"
              disabled={
                loading ||
                (isQuestion ? !questionTitle.trim() : !content.trim())
              }
            >
              {loading ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditPostModal;
